/**
 * User service for handling user-related CRUD operations
 */

import { api } from './api'
import { User } from '@shared/types'
import { transformUser, BackendUser } from '@/utils/userTransform'
import { 
  type CreateUserData,
  type UpdateUserData
} from '@shared'

// User list response with pagination
export interface UsersListResponse {
  users: BackendUser[]
  pagination: {
    totalItems: number
    currentPage: number
    itemsPerPage: number
    totalPages: number
  }
}

// Single user response
export interface UserResponse {
  user: BackendUser
}

// Create user response
export interface CreateUserResponse {
  message: string
  user: BackendUser
}

// Update user response
export interface UpdateUserResponse {
  message: string
  user: BackendUser
}

// Delete user response
export interface DeleteUserResponse {
  message: string
}

// User list query params
export interface UserListParams {
  page?: number
  limit?: number
  role?: string
  search?: string
  sortBy?: 'firstName' | 'lastName' | 'email' | 'role' | 'createdAt'
  sortOrder?: 'asc' | 'desc'
}

/**
 * User service class for CRUD operations
 */
export class UserService {
  private static readonly USER_ENDPOINTS = {
    USERS: '/users',
    USER_BY_ID: (id: string) => `/users/${id}`,
  } as const

  /**
   * Get all users with optional filtering and pagination
   */
  static async getUsers(params: UserListParams = {}): Promise<{
    users: User[]
    totalItems: number
    currentPage: number
    itemsPerPage: number
    totalPages: number
  }> {
    const queryParams = new URLSearchParams()
    
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    if (params.role) queryParams.append('role', params.role)
    if (params.search) queryParams.append('search', params.search)
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)

    const url = `${this.USER_ENDPOINTS.USERS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    const response = await api.get<UsersListResponse>(url)

    // Transform all users from backend format to frontend format
    const users = response.users.map(transformUser)

    console.log(response)

    return {
      users,
      totalItems: response.pagination.totalItems,
      currentPage: response.pagination.currentPage,
      itemsPerPage: response.pagination.itemsPerPage,
      totalPages: response.pagination.totalPages
    }
  }

  /**
   * Get a single user by ID
   */
  static async getUser(id: string): Promise<{ user: User }> {
    const response = await api.get<UserResponse>(this.USER_ENDPOINTS.USER_BY_ID(id))
    
    // Transform backend user to frontend user format
    const user = transformUser(response.user)
    
    return { user }
  }

  /**
   * Create a new user
   */
  static async createUser(userData: CreateUserData): Promise<{ user: User; message: string }> {
    const response = await api.post<CreateUserResponse>(
      this.USER_ENDPOINTS.USERS,
      userData
    )

    // Transform backend user to frontend user format
    const user = transformUser(response.user)

    return {
      user,
      message: response.message
    }
  }

  /**
   * Update an existing user
   */
  static async updateUser(id: string, userData: UpdateUserData): Promise<{ user: User; message: string }> {
    const response = await api.put<UpdateUserResponse>(
      this.USER_ENDPOINTS.USER_BY_ID(id),
      userData
    )

    // Transform backend user to frontend user format
    const user = transformUser(response.user)

    return {
      user,
      message: response.message
    }
  }

  /**
   * Delete a user
   */
  static async deleteUser(id: string): Promise<{ message: string }> {
    const response = await api.delete<DeleteUserResponse>(
      this.USER_ENDPOINTS.USER_BY_ID(id)
    )

    return {
      message: response.message
    }
  }

  /**
   * Get users by role
   */
  static async getUsersByRole(role: User['role']): Promise<{ users: User[] }> {
    // Map frontend role to backend role format
    const backendRoleMap: Record<User['role'], BackendUser['role']> = {
      'hr_admin': 'HR_ADMIN',
      'general_manager': 'GENERAL_MANAGER',
      'branch_manager': 'BRANCH_MANAGER',
      'system_admin': 'SYSTEM_ADMIN',
      'unassigned': 'UNASSIGNED'
    }

    const response = await this.getUsers({ 
      role: backendRoleMap[role] 
    })

    return {
      users: response.users
    }
  }

  /**
   * Search users by name or email
   */
  static async searchUsers(searchTerm: string, limit?: number): Promise<{ users: User[] }> {
    const response = await this.getUsers({ 
      search: searchTerm,
      limit: limit || 50
    })

    return {
      users: response.users
    }
  }

  /**
   * Get users with pagination helper
   */
  static async getUsersPaginated(page: number = 1, limit: number = 10): Promise<{
    users: User[]
    pagination: {
      current: number
      total: number
      pages: number
      hasNext: boolean
      hasPrev: boolean
    }
  }> {
    const response = await this.getUsers({ page, limit })

    console.log(response)

    return {
      users: response.users,
      pagination: {
        current: response.currentPage,
        total: response.totalItems,
        pages: response.totalPages,
        hasNext: response.currentPage < response.totalPages,
        hasPrev: response.currentPage > 1
      }
    }
  }
}

// Export as default and named export for convenience
export default UserService 